import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import "./GalleryModal.css";

interface GalleryImage {
  id: number;
  imageUrl: string;
  type: string; // room type or property type
  name: string; // room name or property name
  category?: string;
}

interface GalleryModalProps {
  visible: boolean;
  onHide: () => void;
  images: GalleryImage[];
  initialImageIndex?: number;
  data?: any; // Additional data for better display (room or property)
  mode?: "room" | "property"; // Mode to determine display context
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  visible,
  onHide,
  images,
  initialImageIndex = 0,
  data,
  mode = "room",
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [isAnimating, setIsAnimating] = useState(false);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  // Helper functions to extract data description
  const getDescription = (dataItem: any): string => {
    if (!dataItem) return "";
    const candidates = [
      dataItem.description,
      dataItem.Description,
      dataItem.shortDescription,
      dataItem.ShortDescription,
      dataItem.categoryDescription,
      dataItem.CategoryDescription,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "")
        return String(c);
    }
    return "";
  };

  // Constants for thumbnail slider
  const THUMBNAILS_PER_VIEW = 5;
  const showThumbnailSlider = images.length > THUMBNAILS_PER_VIEW;

  // Handle image load errors
  const handleImageError = (index: number) => {
    setImageErrors((prev) => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
  };

  // Update current image index when modal opens with initial index
  useEffect(() => {
    if (visible) {
      setCurrentImageIndex(initialImageIndex);
      // Auto-scroll thumbnail view to show current image
      if (showThumbnailSlider) {
        const newStartIndex = Math.max(
          0,
          Math.min(
            initialImageIndex - Math.floor(THUMBNAILS_PER_VIEW / 2),
            images.length - THUMBNAILS_PER_VIEW
          )
        );
        setThumbnailStartIndex(newStartIndex);
      }
    }
  }, [visible, initialImageIndex, showThumbnailSlider, images.length]);

  const handlePreviousImage = () => {
    if (images.length === 0 || isAnimating) return;

    const prevIndex =
      currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;

    setCurrentImageIndex(prevIndex);

    // Auto-scroll thumbnail view
    if (showThumbnailSlider) {
      autoScrollThumbnails(prevIndex);
    }
  };

  const handleNextImage = () => {
    if (images.length === 0 || isAnimating) return;

    const nextIndex =
      currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;

    setCurrentImageIndex(nextIndex);

    // Auto-scroll thumbnail view
    if (showThumbnailSlider) {
      autoScrollThumbnails(nextIndex);
    }
  };

  // Add keyboard navigation support
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!visible || isAnimating) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePreviousImage();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNextImage();
          break;
        case "Escape":
          event.preventDefault();
          onHide();
          break;
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [visible, currentImageIndex, images.length, onHide, isAnimating]);

  const handleThumbnailClick = (targetIndex: number) => {
    if (targetIndex === currentImageIndex || isAnimating) return;

    const distance = Math.abs(targetIndex - currentImageIndex);

    // If jumping more than 1 image, animate through intermediate images
    if (distance > 1) {
      animateToImage(targetIndex);
    } else {
      setCurrentImageIndex(targetIndex);
    }

    // Auto-scroll thumbnail view to keep current image visible
    if (showThumbnailSlider) {
      autoScrollThumbnails(targetIndex);
    }
  };

  // Auto-scroll thumbnails to keep current image in view
  const autoScrollThumbnails = (imageIndex: number) => {
    const currentStart = thumbnailStartIndex;
    const currentEnd = currentStart + THUMBNAILS_PER_VIEW - 1;

    // If image is outside current view, scroll to center it
    if (imageIndex < currentStart || imageIndex > currentEnd) {
      const newStartIndex = Math.max(
        0,
        Math.min(
          imageIndex - Math.floor(THUMBNAILS_PER_VIEW / 2),
          images.length - THUMBNAILS_PER_VIEW
        )
      );
      setThumbnailStartIndex(newStartIndex);
    }
  };

  // Handle thumbnail slider navigation
  const handleThumbnailPrevious = () => {
    if (thumbnailStartIndex > 0 && !isSliding) {
      setIsSliding(true);
      setThumbnailStartIndex(Math.max(0, thumbnailStartIndex - 1));
      setTimeout(() => setIsSliding(false), 400);
    }
  };

  const handleThumbnailNext = () => {
    const maxStartIndex = images.length - THUMBNAILS_PER_VIEW;
    if (thumbnailStartIndex < maxStartIndex && !isSliding) {
      setIsSliding(true);
      setThumbnailStartIndex(Math.min(maxStartIndex, thumbnailStartIndex + 1));
      setTimeout(() => setIsSliding(false), 400);
    }
  };

  // Animate through images with intermediate steps
  const animateToImage = (targetIndex: number) => {
    setIsAnimating(true);
    const startIndex = currentImageIndex;
    const direction = targetIndex > startIndex ? 1 : -1;
    const steps = Math.abs(targetIndex - startIndex);

    let currentStep = 0;
    // Dynamic speed: faster for longer distances, slower for short ones
    const baseSpeed = 250;
    const animationSpeed = Math.max(150, baseSpeed - steps * 20); // Speed up for longer distances

    const animate = () => {
      if (currentStep < steps) {
        const nextIndex = startIndex + direction * (currentStep + 1);
        setCurrentImageIndex(nextIndex);
        currentStep++;

        setTimeout(animate, animationSpeed);
      } else {
        setIsAnimating(false);
      }
    };

    animate();
  };

  const currentImage = images[currentImageIndex];

  // Handle empty state
  if (!visible || images.length === 0) return null;

  // Handle loading or invalid image state
  if (!currentImage) {
    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        modal
        closable={false}
        className="room-gallery-modal"
        maskClassName="room-gallery-mask"
        style={{ width: "100vw", height: "100vh", maxWidth: "100%" }}
        contentClassName="room-gallery-content"
      >
        <div className="room-gallery-container">
          <div className="room-gallery-header">
            <div />
            <Button
              icon="pi pi-times"
              className="room-gallery-close-btn"
              onClick={onHide}
              rounded
              text
              size="large"
            />
          </div>
          <div className="room-gallery-main">
            <div className="room-gallery-left">
              <div className="room-gallery-main-image">
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <i
                      className="pi pi-spin pi-spinner"
                      style={{ fontSize: "2rem", color: "#6b7280" }}
                    ></i>
                    <p className="mt-2 text-muted">Loading images...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      closable={false}
      className="room-gallery-modal"
      maskClassName="room-gallery-mask"
      style={{ width: "100vw", height: "100vh", maxWidth: "100%" }}
      contentClassName="room-gallery-content"
    >
      <div className="room-gallery-container">
        {/* Header with only close button (title moved to right sidebar) */}
        <div className="room-gallery-header">
          <div />
          <Button
            icon="pi pi-times"
            className="room-gallery-close-btn"
            onClick={onHide}
            rounded
            text
            size="large"
          />
        </div>

        {/* Main content area with right sidebar for title + description */}
        <div className="room-gallery-main">
          {/* Left: Image and thumbnails */}
          <div className="room-gallery-left">
            {/* Main image display */}
            <div className="room-gallery-main-image">
              {currentImage && (
                <>
                  {imageErrors.has(currentImageIndex) ? (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div className="text-center">
                        <i
                          className="pi pi-image"
                          style={{ fontSize: "3rem", color: "#6b7280" }}
                        ></i>
                        <p className="mt-2 text-muted">
                          Image could not be loaded
                        </p>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={currentImage.imageUrl}
                      alt={currentImage.name}
                      className={`main-room-image ${
                        isAnimating ? "animating" : ""
                      }`}
                      onError={() => handleImageError(currentImageIndex)}
                    />
                  )}
                </>
              )}

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <Button
                    icon="pi pi-chevron-left"
                    className="room-gallery-nav-btn room-gallery-nav-prev"
                    onClick={handlePreviousImage}
                    disabled={isAnimating}
                    rounded
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    className="room-gallery-nav-btn room-gallery-nav-next"
                    onClick={handleNextImage}
                    disabled={isAnimating}
                    rounded
                  />
                </>
              )}
            </div>

            {/* Bottom section with thumbnails */}
            {images.length > 1 && (
              <div className="room-gallery-bottom">
                {/* Thumbnail images with slider */}
                <div className="thumbnail-slider-container">
                  {showThumbnailSlider && (
                    <Button
                      icon="pi pi-chevron-left"
                      className="thumbnail-nav-btn thumbnail-nav-prev"
                      onClick={handleThumbnailPrevious}
                      disabled={thumbnailStartIndex === 0 || isSliding}
                      rounded
                      text
                    />
                  )}

                  <div
                    className={`room-gallery-thumbnails ${
                      isSliding ? "sliding" : ""
                    }`}
                  >
                    {(showThumbnailSlider
                      ? images.slice(
                          thumbnailStartIndex,
                          thumbnailStartIndex + THUMBNAILS_PER_VIEW
                        )
                      : images
                    ).map((image, displayIndex) => {
                      const actualIndex = showThumbnailSlider
                        ? thumbnailStartIndex + displayIndex
                        : displayIndex;
                      return (
                        <div
                          key={image.id}
                          className={`thumbnail-item ${
                            actualIndex === currentImageIndex ? "active" : ""
                          } ${isAnimating ? "animating" : ""}`}
                          onClick={() => handleThumbnailClick(actualIndex)}
                          style={{
                            cursor: isAnimating ? "wait" : "pointer",
                            opacity:
                              isAnimating && actualIndex !== currentImageIndex
                                ? 0.6
                                : 1,
                          }}
                        >
                          {imageErrors.has(actualIndex) ? (
                            <div className="thumbnail-image d-flex align-items-center justify-content-center bg-light">
                              <i
                                className="pi pi-image"
                                style={{ fontSize: "1.5rem", color: "#6b7280" }}
                              ></i>
                            </div>
                          ) : (
                            <img
                              src={image.imageUrl}
                              alt={image.name}
                              className="thumbnail-image"
                              onError={() => handleImageError(actualIndex)}
                            />
                          )}
                          <div className="thumbnail-label">
                            <span className="thumbnail-name">
                              Image {actualIndex + 1}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {showThumbnailSlider && (
                    <Button
                      icon="pi pi-chevron-right"
                      className="thumbnail-nav-btn thumbnail-nav-next"
                      onClick={handleThumbnailNext}
                      disabled={
                        thumbnailStartIndex >=
                          images.length - THUMBNAILS_PER_VIEW || isSliding
                      }
                      rounded
                      text
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sidebar with title + description */}
          {currentImage && (
            <aside className="room-gallery-sidebar">
              <div className="room-description-section">
                <div className="room-description-header">
                  <h4 className="room-description-title">
                    {currentImage.name ||
                      (mode === "property"
                        ? "Property Gallery"
                        : "Room Gallery")}
                    {isAnimating && (
                      <i
                        className="pi pi-spin pi-spinner ms-2"
                        style={{ fontSize: "0.8rem", color: "#6b7280" }}
                      ></i>
                    )}
                  </h4>
                  <span className="room-type-badge">
                    {currentImage.type}
                    {images.length > 1 && (
                      <span className="ms-2">
                        ({currentImageIndex + 1} of {images.length})
                      </span>
                    )}
                    {images.length === 1 && (
                      <span className="ms-2">• Single Image</span>
                    )}
                  </span>
                </div>
                {data && (
                  <div className="room-description-content">
                    <p className="room-description-text">
                      {getDescription(data)}
                    </p>
                    {mode === "property" && data?.location && (
                      <div className="room-info-item">
                        <i
                          className="pi pi-map-marker"
                          style={{ fontSize: "14px", marginRight: "8px" }}
                        ></i>
                        <span>
                          Location: {data.location?.name}, {data.city?.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default GalleryModal;
